import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, {
  parsePhoneNumber,
  FeatureProps as PhoneInputProps,
} from 'react-phone-number-input';

import { FormControl, FormControlProps, FormHelperText } from '@mui/material';
import { useField, useFormikContext } from 'formik';

import { SimpleError } from '../error/SimpleError';
import type { TErrorProps } from '../error/types';
import { PhoneCountry } from './PhoneCountry';
import { PhoneNumber } from './PhoneNumber';

type TFormikPhoneProps = PhoneInputProps<{
  name: string;
  label: string;
  countryLabel?: string;
  'data-testid'?: string;
  margin?: FormControlProps['margin'];
  HelperText?: (props: TErrorProps) => JSX.Element;
}>;

export const FormikPhone = ({
  name,
  margin,
  label: labelKey,
  countryLabel = '',
  'data-testid': testId,
  HelperText = SimpleError,
  ...props
}: TFormikPhoneProps) => {
  const [field, meta, helpers] = useField<string>(name);
  const [country, setCountry] = useState(() => parsePhoneNumber(field.value)?.country ?? 'US');

  const { submitCount } = useFormikContext();
  const { t } = useTranslation();

  const label = t(labelKey);
  const errorShown = Boolean(meta.error) && submitCount > 0;

  return (
    <FormControl margin={margin}>
      <PhoneInput
        name={name}
        value={field.value}
        defaultCountry={country}
        onChange={(value = '') => helpers.setValue(value)}
        onCountryChange={(newCountry = country) => setCountry(newCountry)}
        countrySelectComponent={PhoneCountry}
        inputComponent={PhoneNumber}
        international={false}
        style={{
          display: 'flex',
        }}
        countrySelectProps={{
          label: t(countryLabel),
          labelId: `label-for-${name}-country`,
          defaultValue: country,
          error: errorShown,
        }}
        numberInputProps={{
          inputProps: {
            'data-testid': testId,
          },
          error: errorShown,
          label,
        }}
        limitMaxLength
        {...props}
      />

      <FormHelperText component="div">
        <HelperText error={meta.error} errorsShown={errorShown} label={label} />
      </FormHelperText>
    </FormControl>
  );
};
